<template>
  <section id="avond" :class="[$style['dates'], contained && $style['dates--contained']]" aria-labelledby="avond-title">
    <div :class="$style['dates__head']">
      <h2 id="avond-title" :class="$style['dates__title']">Kies je avond</h2>
      <p>Kaarten bestel je bij Schouwburg Hengelo. Kies een datum en je gaat meteen naar de bestelpagina.</p>
    </div>
    <ol v-if="performances.length" :class="$style['dates__list']">
      <li v-for="(performance, i) in performances" :key="performance.id">
        <a
          :href="performance.url"
          :class="$style['date']"
          :aria-label="performance.label"
          @pointerenter="showPeek(i)"
          @pointerleave="hidePeek">
          <span :class="$style['date__number']">{{ performance.day }} <small>{{ performance.month }}</small></span>
          <span :class="$style['date__meta']">
            <b v-if="performance.note">{{ performance.note }}</b>
            {{ performance.weekday }}, {{ performance.time }}
          </span>
          <span :class="$style['date__cta']">Kaarten</span>
        </a>
      </li>
    </ol>
    <p v-else :class="$style['dates__empty']">De speeldata van de volgende revue volgen snel.</p>
    <img ref="peek" :class="[$style['peek'], peekOn && $style['peek--on']]" :src="peekSrc" alt="" aria-hidden="true">
  </section>
</template>
<script setup lang="ts">
import type { Performance } from '~/composables/usePerformances'

// The six evenings as giant rows. Hovering floods a row amber and a production photo follows the cursor.
// contained: on an inner page the rows line up with the page column instead of the screen edge
const props = defineProps<{ performances: Array<Performance>, photos: Array<string>, contained?: boolean }>()

const image = useImage()
const peekPhotos = computed(() => props.photos.map(src => image(src, { width: 480, height: 640, format: 'webp' })))
const peek = ref<HTMLImageElement>()
const peekSrc = ref<string>()
const peekOn = ref(false)
let enabled = false
let frame = 0
let mouseX = 0
let mouseY = 0
let peekX = 0
let peekY = 0

function showPeek (index: number) {
  if (!enabled) return
  peekSrc.value = peekPhotos.value[index % peekPhotos.value.length]
  if (!peekOn.value) {
    peekX = mouseX + 150
    peekY = mouseY
  }
  peekOn.value = true
}

function hidePeek () {
  peekOn.value = false
}

function onMove (event: PointerEvent) {
  mouseX = event.clientX
  mouseY = event.clientY
}

function follow () {
  peekX += (mouseX + 150 - peekX) * 0.14
  peekY += (mouseY - peekY) * 0.14
  if (peek.value) peek.value.style.translate = `${peekX}px ${peekY}px`
  frame = requestAnimationFrame(follow)
}

onMounted(() => {
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (!finePointer || reduce || !peekPhotos.value.length) return
  enabled = true
  window.addEventListener('pointermove', onMove, { passive: true })
  frame = requestAnimationFrame(follow)
})

onBeforeUnmount(() => {
  cancelAnimationFrame(frame)
  window.removeEventListener('pointermove', onMove)
})
</script>
<style module>
.dates {
  padding: var(--stage-section) var(--stage-gutter);
  background: var(--stage-black);
  color: var(--stage-white);
}

.dates--contained {
  padding-top: var(--stage-rhythm);
  padding-inline: max(var(--stage-gutter), (100% - var(--container-width)) / 2 + var(--stage-gutter));
}

.dates__head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--spacing-xl);
  margin-bottom: var(--spacing-xxxl);
}

.dates__head p {
  max-width: 24rem;
  margin: 0;
  color: var(--stage-soft);
}

.dates__title {
  margin: 0;
  font-family: var(--font-family-heading);
  font-weight: var(--font-weight-bold);
  font-size: var(--stage-title);
  line-height: 0.88;
  letter-spacing: -0.035em;
  text-transform: uppercase;
}

/* Under the page title on an inner page: an ordinary section heading, the dates carry the section */
.dates--contained .dates__title {
  font-size: var(--stage-heading);
}

.dates__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.dates__empty {
  font-size: var(--font-size-large);
}

.date {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--spacing-l) var(--spacing-xxl);
  margin-inline: calc(-1 * var(--spacing-l));
  padding: clamp(1rem, 0.6rem + 1.4vw, 2rem) var(--spacing-l);
  border-top: var(--stage-hairline) solid var(--stage-line);
  color: var(--stage-white);
  text-decoration: none;
  transition: color 250ms;
}

.date::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  background: var(--primary-color);
  transform: scaleY(0);
  transform-origin: 50% 100%;
  transition: transform 450ms var(--stage-ease);
}

.date:hover,
.date:focus-visible {
  color: var(--stage-black);
}

.date:hover::before,
.date:focus-visible::before {
  transform: scaleY(1);
}

.dates__list > li:last-child > .date {
  border-bottom: var(--stage-hairline) solid var(--stage-line);
}

.date__number {
  display: flex;
  align-items: baseline;
  gap: 0.18em;
  font-family: var(--font-family-heading);
  font-weight: var(--font-weight-bold);
  font-size: clamp(3rem, 1.4rem + 7vw, 9rem);
  line-height: 0.85;
  letter-spacing: -0.045em;
  text-transform: uppercase;
}

.date__number small {
  font-size: 0.42em;
  letter-spacing: -0.01em;
}

/* Inner page: the dates stay big, but below the page title */
.dates--contained .date__number {
  font-size: clamp(2.5rem, 1rem + 3vw, 4rem);
}

.date__meta {
  font-size: clamp(1rem, 0.9rem + 0.5vw, 1.375rem);
  line-height: 1.35;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.date__meta b {
  display: block;
  letter-spacing: 0.02em;
}

.date__cta {
  padding: var(--spacing-l) var(--spacing-xl);
  border: 0.125rem solid currentcolor;
  border-radius: 999rem;
  font-weight: var(--font-weight-bold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.peek {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 800;
  width: clamp(12rem, 17vw, 17rem);
  aspect-ratio: 3 / 4;
  object-fit: cover;
  pointer-events: none;
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.7) rotate(-6deg);
  transition: opacity 250ms, transform 350ms var(--stage-ease);
  box-shadow: 0 2rem 4rem oklch(0% 0 0deg / 60%);
}

.peek:not([src]) {
  visibility: hidden;
}

.peek--on {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1) rotate(-3deg);
}

@media (width <= 48rem) {
  .date {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .date__meta {
    grid-column: 1;
    grid-row: 2;
  }

  .date__cta {
    grid-column: 2;
    grid-row: 1 / span 2;
  }
}

@media (hover: none) {
  .peek {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .date,
  .date::before,
  .peek {
    transition: none;
  }
}
</style>
